// DriverTransferNotes — the kitchen deliveries a driver carries, each with its delivery note (PDF) one tap away.
//
// The list is the driver's own inbox (GET /api/receiving/receipts, which for a driver returns only the transfers
// going to their assigned site, in the inbox shape: no prices). The note is the UNPRICED one
// (GET /api/ck-ops/transfers/{id}/delivery-note.pdf, backend/ck_ops/billing_api.py). The priced charge sheet has no
// call here on purpose: the server refuses it to a driver and this screen never offers it.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FileText, Loader2, Package } from 'lucide-react';
import { apiBase } from '../../utils/authHeaders';
import { openPdfBlob } from '../../utils/openPdfBlob';
import { detailOf } from '../../invoices/api';

const API_URL = apiBase();

const STATUS_LABEL = { open: 'On the road', confirmed: 'Delivered' };

function when(value) {
  if (!value) return 'Not recorded';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

async function failureOf(res, fallback) {
  let data = null;
  try { data = JSON.parse(await res.text()); } catch { data = null; }
  return detailOf(data, `${fallback} (${res.status})`);
}

function NoteButton({ transferId, headers, isCurrent }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const locked = useRef(false);

  const open = async () => {
    if (locked.current) return;
    locked.current = true; setBusy(true); setError('');
    try {
      const res = await fetch(`${API_URL}/api/ck-ops/transfers/${encodeURIComponent(transferId)}/delivery-note.pdf`, { headers });
      if (!res.ok) throw new Error(await failureOf(res, 'The delivery note could not be opened'));
      const blob = await res.blob();
      if (!isCurrent()) return;
      openPdfBlob(blob, { what: 'The delivery note' });
    } catch (e) {
      if (isCurrent()) setError(e.message || 'The delivery note could not be opened');
    } finally {
      locked.current = false;
      if (isCurrent()) setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button" onClick={open} disabled={busy} aria-busy={busy}
        aria-label={`Delivery note (PDF) ${transferId}`}
        className="mt-3 w-full min-h-[44px] px-3 py-2 text-sm rounded-md border border-primary-500 text-primary-700 bg-surface hover:bg-primary-50 disabled:opacity-40 motion-organic"
        data-testid={`driver-transfer-${transferId}-note`}
      >
        {busy ? <Loader2 className="w-4 h-4 inline animate-spin mr-1" /> : <FileText className="w-4 h-4 inline mr-1" />}
        Delivery note (PDF)
      </button>
      {error ? (
        <p className="mt-1 text-xs text-danger" role="alert" data-testid={`driver-transfer-${transferId}-note-error`}>{error}</p>
      ) : null}
    </>
  );
}

export default function DriverTransferNotes({ headers, scopeKey }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const scopeRef = useRef(scopeKey);
  scopeRef.current = scopeKey;

  const load = useCallback(async () => {
    const forScope = scopeKey;
    setLoading(true); setError(''); setRows([]);
    try {
      const res = await fetch(`${API_URL}/api/receiving/receipts?mine=false&limit=100`, { headers });
      if (!res.ok) throw new Error(await failureOf(res, 'Kitchen deliveries could not be loaded'));
      const list = await res.json();
      if (scopeRef.current !== forScope) return;   // the site changed while this was in flight
      setRows((Array.isArray(list) ? list : []).filter((r) => r && r.kind === 'transfer'));
    } catch (e) {
      if (scopeRef.current === forScope) setError(e.message || 'Kitchen deliveries could not be loaded');
    } finally {
      if (scopeRef.current === forScope) setLoading(false);
    }
  }, [headers, scopeKey]);

  useEffect(() => { load(); }, [load]);

  const isCurrent = useCallback(() => scopeRef.current === scopeKey, [scopeKey]);

  return (
    <section aria-label="Kitchen deliveries" className="space-y-3" data-testid="driver-transfers">
      <h2 className="font-display text-sm uppercase tracking-overline text-ink-muted flex items-center gap-1.5 pt-2">
        <Package className="w-3.5 h-3.5" /> Kitchen deliveries
      </h2>
      {loading ? (
        <div className="text-center py-4 text-ink-muted"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div>
      ) : error ? (
        <div className="bg-surface border border-danger/30 rounded-md p-4 text-center" role="alert" data-testid="driver-transfers-error">
          <p className="text-sm text-ink">{error}</p>
        </div>
      ) : rows.length === 0 ? (
        <p className="text-xs text-ink-muted text-center py-2" data-testid="driver-transfers-empty">No kitchen deliveries for this site.</p>
      ) : rows.map((t) => (
        <article key={t.id} className="bg-surface border border-surface-border rounded-lg shadow-ambient p-4" data-testid={`driver-transfer-${t.id}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-overline text-ink-muted">Transfer</div>
              <h3 className="font-display text-base text-ink mt-0.5 break-all">{t.po_number || t.id}</h3>
            </div>
            <span className="text-xs px-2 py-0.5 rounded-md border border-surface-border text-ink-muted font-medium">
              {STATUS_LABEL[t.status] || t.status}
            </span>
          </div>
          <p className="mt-2 text-xs text-ink-muted">
            From {t.supplier_name || 'the kitchen'} · Dispatched {when(t.arrived_at)} · {(t.lines || []).length} line{(t.lines || []).length === 1 ? '' : 's'}
          </p>
          <NoteButton transferId={t.id} headers={headers} isCurrent={isCurrent} />
        </article>
      ))}
    </section>
  );
}
